import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectRouteParams } from 'src/app/app.selectors';
import {
   accountDetailLoad,
   loadAccountDetailsSuccess,
} from 'src/app/pages/account-details/state/account-detail.actions';
import * as AppStartActions from '../../app-start/app-start.actions';
import * as AuthenticationActions from '../../authentication/state/authentication.actions';
import * as SelectAccountActions from './select-account.actions';
import { accountDetailSpecificActions } from './select-account.reducer';
import { getSelectedAccountId } from './select-account.selector';
import { SelectAccountSessionStorageService } from './services/select-account-session-storage.service';

@Injectable()
export class SelectAccountEffects {
   loggedOut = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AppStartActions.authenticationVerificationFailure, AuthenticationActions.apiLogoutSuccess),
            tap((action) => {
               this.selectAccountSessionStorageService.clearAll();
            })
         );
      },
      { dispatch: false }
   );

   accountSelected = createEffect(() => {
      return this.actions$.pipe(
         ofType(SelectAccountActions.selectAccount),
         tap((state) => {
            this.selectAccountSessionStorageService.setSelectedAccount(state);
         }),
         map((action) => {
            return SelectAccountActions.selectedAccountLoaded(action);
         })
      );
   });

   accountDetailPageLoad = createEffect(() =>
      this.actions$.pipe(
         ofType(accountDetailLoad),
         withLatestFrom(this.store.pipe(select(getSelectedAccountId)), this.store.pipe(select(selectRouteParams))),
         filter(([_, oldAccountId, params]) => {
            const id = parseInt(params.id, 10);
            return !isNaN(id) && id !== oldAccountId;
         }),
         map(() => SelectAccountActions.accountIdChange())
      )
   );

   accountSpecificPageLoad = createEffect(() =>
      this.actions$.pipe(
         ofType(...accountDetailSpecificActions),
         withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
         filter(([action, oldAccountId]) => {
            const id = action.data.account?.accountId;
            return !isNaN(id) && id !== oldAccountId;
         }),
         map(() => SelectAccountActions.accountIdChange())
      )
   );

   accountDetailLoadSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(loadAccountDetailsSuccess),
            tap((state) => {
               this.selectAccountSessionStorageService.setSelectedAccount({
                  accountId: state.data.accountId,
                  accountName: state.data.name,
                  accountShortName: state.data.shortName,
                  timeZoneId: state.data.timeZoneId,
               });
            })
         );
      },
      { dispatch: false }
   );

   accounSpecificPageLoadSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(...accountDetailSpecificActions),
            tap((state) => {
               const account = state?.data.account;

               if (account && 'shortName' in account) {
                  this.selectAccountSessionStorageService.setSelectedAccount({
                     accountId: account.accountId ?? null,
                     accountName: account.name ?? null,
                     accountShortName: account.shortName ?? null,
                     timeZoneId: null,
                  });
               } else {
                  this.selectAccountSessionStorageService.setSelectedAccount({
                     accountId: account?.accountId ?? null,
                     accountName: account?.name ?? null,
                     accountShortName: null,
                     timeZoneId: null,
                  });
               }
            })
         );
      },
      { dispatch: false }
   );

   // systemPageLoadSuccess = createEffect(
   //    () => {
   //       return this.actions$.pipe(
   //          ofType(...systemActions),
   //          tap((state) => {
   //             this.selectAccountSessionStorageService.setSelectedAccount({
   //                accountId: null,
   //                accountName: null,
   //                accountShortName: null,
   //             });
   //          })
   //       );
   //    },
   //    { dispatch: false }
   // );

   restoredSession = createEffect(() => {
      return this.actions$.pipe(
         ofType(AppStartActions.authenticationVerificationSuccess, AuthenticationActions.loginSuccess),
         map(({ loginResult }) => {
            const cachedAccount = this.selectAccountSessionStorageService.getSelectedAccount();
            if (cachedAccount) {
               return SelectAccountActions.selectedAccountLoaded(cachedAccount);
            } else {
               return SelectAccountActions.selectedAccountLoadedOnLogin({
                  accountId: loginResult.AccountId,
                  accountName: loginResult.AccountName,
                  accountShortName: loginResult.Shortname,
                  isSystemUser: loginResult.IsSystemUser,
                  timeZoneId: loginResult.TimeZoneId,
               });
            }
         })
      );
   });

   constructor(
      private selectAccountSessionStorageService: SelectAccountSessionStorageService,
      private actions$: Actions,
      private store: Store<AppState>
   ) {}
}
